import * as React from "react";
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  selectAllDriversOrderByHavingConflictingSchedulesWithRoute,
  selectDriver,
  selectDriverRoutesConflict,
  selectDriverSchedulesConflictingWithRoute,
} from "../../../ducks/data/drivers/selectors";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { DriverScheduleText } from "../../../pages/FleetManagementPage/DriversTab/DriverSchedule/DriverScheduleButtonWithDialog";
import clsx from "clsx";
import "./AddDriver.scss";
import { useFeature } from "../../../hooks";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const DriversList = ({
  routeId,
  onDriverClick,
  routeDrivers,
  pendingDriverChanges,
}: {
  routeId: string;
  onDriverClick: (driverId: string, checked: boolean) => void;
  routeDrivers: string[] | null;
  pendingDriverChanges: Record<string, boolean>;
}) => {
  const { t } = useTranslation("dispatching");
  const [search, setSearch] = React.useState("");
  const firstItemRef = React.useRef<HTMLLIElement | null>(null);
  // const drivers = useSelector(selectDrivers);
  const drivers = useSelector(
    selectAllDriversOrderByHavingConflictingSchedulesWithRoute(routeId)
  );

  const lowerSearch = search.toLowerCase();
  const filteredDrivers = drivers
    .filter((x) => x.active)
    .filter(
      (driver) =>
        !lowerSearch ||
        driver.firstName.toLowerCase().includes(lowerSearch) ||
        driver.lastName.toLowerCase().includes(lowerSearch) ||
        driver.number.toLowerCase().includes(lowerSearch)
    );

  const autoFocusInput = drivers.length > 10;

  return (
    <>
      {drivers.length > 10 && (
        <TextField
          size="small"
          autoFocus={autoFocusInput}
          placeholder={t("routeCard.selectDriver")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "ArrowDown" && firstItemRef.current) {
              e.preventDefault();
              firstItemRef.current.focus();
            }
          }}
          fullWidth
          sx={{ px: 1, pb: 1 }}
        />
      )}
      {filteredDrivers.map((driver, i) => (
        <DriverMenuItem
          autoFocus={i === 0 && !autoFocusInput}
          itemRef={i === 0 ? firstItemRef : undefined}
          key={driver.id}
          routeId={routeId}
          driverId={driver.id}
          checked={
            pendingDriverChanges[driver.id] ??
            (routeDrivers?.includes(driver.id) || false)
          }
          reqInProgress={driver.id in pendingDriverChanges}
          onDriverClick={onDriverClick}
        />
      ))}
    </>
  );
};

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "white",
    border: "1px solid #dadde9",
  },
}));

const DriverMenuItem = (props: {
  routeId: string;
  driverId: string;
  checked: boolean;
  reqInProgress: boolean;
  autoFocus?: boolean;
  itemRef?: React.Ref<HTMLLIElement>;
  onDriverClick: (driverId: string, checked: boolean) => void;
}) => {
  const {
    routeId,
    driverId,
    checked,
    reqInProgress,
    onDriverClick,
    autoFocus,
    itemRef,
  } = props;
  const { t } = useTranslation("dispatching");
  const driversSchedulesEnabled = useFeature("drivers-schedules");
  const driver = useSelector(selectDriver(driverId));
  const { conflictingSchedules, schedulesOnRouteDays } = useSelector(
    selectDriverSchedulesConflictingWithRoute(driverId, routeId)
  );
  const hasRoutesConflict = useSelector(
    selectDriverRoutesConflict(driverId, routeId)
  );
  if (!driver) {
    return null;
  }
  const labelId = `checkbox-list-label-${routeId}-${driver.id}`;
  const classes = clsx("add-driver__menu-item", {
    "add-driver__menu-item--days-intersect":
      !conflictingSchedules.length && schedulesOnRouteDays.length,
    "add-driver__menu-item--conflicting": conflictingSchedules.length,
    "add-driver__menu-item--conflicting-blue":
      hasRoutesConflict && !conflictingSchedules.length,
  });

  const baseElement = (
    <MenuItem
      key={driver.id}
      ref={itemRef}
      onClick={() => onDriverClick(driver.id, !checked)}
      className={classes}
      autoFocus={autoFocus}
      disabled={reqInProgress}
    >
      <Checkbox
        icon={icon}
        checkedIcon={checkedIcon}
        style={{ marginRight: 8 }}
        checked={checked}
        inputProps={{ "aria-labelledby": labelId }}
      />
      <ListItemText
        id={labelId}
        primary={`${driver.firstName} ${driver.lastName}`}
        secondary={`${t("routeCard.driverNumber")} ${driver.number}`} // at some point a driver should be connected to a department, and this could potentially be shown here
      />
    </MenuItem>
  );

  let arrayToDisplayInTooltip = conflictingSchedules.length
    ? conflictingSchedules
    : schedulesOnRouteDays.length
      ? schedulesOnRouteDays
      : null;

  return arrayToDisplayInTooltip && driversSchedulesEnabled ? (
    <HtmlTooltip
      title={
        <div className="flex flex-col gap-2">
          {arrayToDisplayInTooltip.map((schedule) => (
            <div className="add-driver__conflicting-schedule" key={schedule.id}>
              {schedule.absenceNote ? (
                <div className="add-driver__conflicting-schedule-header">
                  {schedule.absenceNote}
                </div>
              ) : null}
              <DriverScheduleText schedule={schedule} />
            </div>
          ))}
        </div>
      }
      placement="right"
    >
      {baseElement}
    </HtmlTooltip>
  ) : (
    baseElement
  );
};

const AddDrivers = (props: {
  routeId: string;
  onDriverClick: (driverId: string, checked: boolean) => Promise<unknown>;
  routeDrivers: string[] | null;
}) => {
  const { routeId, routeDrivers } = props;
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLButtonElement | null>(null);

  const handleClick = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // The route's driver list only updates once the server answers, so on a
  // slow request the reopened menu would still offer the old state and a
  // second click sent the same change again, racing the route's optimistic
  // lock into a 409 (DISPATCHER-8K). While a driver's change is in flight, show
  // it as done and don't let it be clicked again. This lives here rather than
  // in the menu because the menu unmounts when it closes.
  const pendingDriverChangesRef = React.useRef<Record<string, boolean>>({});
  const [pendingDriverChanges, setPendingDriverChanges] = React.useState<
    Record<string, boolean>
  >({});
  const setPending = (next: Record<string, boolean>) => {
    pendingDriverChangesRef.current = next;
    setPendingDriverChanges(next);
  };

  const onDriverClick = async (driverId: string, checked: boolean) => {
    setOpen(false);
    if (driverId in pendingDriverChangesRef.current) {
      return;
    }
    setPending({ ...pendingDriverChangesRef.current, [driverId]: checked });
    try {
      await props.onDriverClick(driverId, checked);
    } finally {
      const { [driverId]: _done, ...rest } = pendingDriverChangesRef.current;
      setPending(rest);
    }
  };

  const { t } = useTranslation("dispatching");
  return (
    <div>
      <Tooltip title={t("routeCard.addDriver")}>
        <IconButton
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          onClick={handleClick}
          ref={ref}
          sx={{ padding: 0 }}
        >
          <PersonAddAlt1Icon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={ref.current}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: "24ch",
          },
        }}
      >
        <DriversList
          onDriverClick={onDriverClick}
          routeId={routeId}
          routeDrivers={routeDrivers}
          pendingDriverChanges={pendingDriverChanges}
        />
      </Menu>
    </div>
  );
};

export default AddDrivers;
